import React, { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react';
import { Link } from 'gatsby-plugin-react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigationMenu } from '../hooks/useNavigationMenu';
import menuDropdownIcon from '@icons/menuDropdownIcon';
import menuDropdownIconBlack from '@icons/menuDropdownIconBlack';
import { SubMenu } from './SubMenu';
import { SubMenuWithBack } from './SubMenuWithBack';
import { useLocation } from '@reach/router';

import * as styles from './style.module.scss';
import * as subMenuStyles from './SubMenu/style.module.scss';

const { navMenu, navMenu__group, isActive, navMenu__button, navMenu__link } =
  styles;

const { isOpened, hasNavVisible } = subMenuStyles;

export const NavMenu = forwardRef(({ headerRef, isNavOpened, isSubMenuOpened, setIsSubMenuOpened, isScrolled }, ref) => {
  const menu = useNavigationMenu();
  const submenuElements = useRef([]);
  const [openedSubmenuIndex, setOpenedSubmenuIndex] = useState(-1);
  const [navStack, setNavStack] = useState([]);
  const currentLevel = navStack[navStack.length - 1];
  const [isMobileView, setIsMobileView] = useState(false);
  const [isStreamPage, setIsStreamPage] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      setIsStreamPage(path.startsWith('/resources/stream/'));
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1140);
    };

    handleResize(); // set on load
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const goToSubmenu = (submenu) => {
    if (isMobileView && navStack.length === 0) {
      setNavStack([submenu]);
      setIsSubMenuOpened(true);
    }
  };


  const goBack = () => {
    setNavStack([]);
    setIsSubMenuOpened(false);
  };
  
  useImperativeHandle(ref, () => ({
    goBackToRoot: goBack,
  }));

  useEffect(() => {
    if (!isNavOpened) {
      setNavStack([]);
      setIsSubMenuOpened(false);
    }
  }, [isNavOpened]);


  const handleLinkClick = () => {
    setOpenedSubmenuIndex(-1);
    setIsSubMenuOpened(false);
  };

  const handleButtonClick = (index) => {
    let openedSubmenuHeight = 0;

    if (index !== openedSubmenuIndex) {
      openedSubmenuHeight = submenuElements?.current[index].offsetHeight;
      setOpenedSubmenuIndex(index);
      setIsSubMenuOpened(true);
    } else {
      setOpenedSubmenuIndex(-1);
      setIsSubMenuOpened(false);
    }

    headerRef.current.style.setProperty(
      '--top-navigation-submenu-height',
      `${openedSubmenuHeight}px`
    );
  };

  useEffect(() => {
    if (!isNavOpened) {
      setOpenedSubmenuIndex(-1);
      setIsSubMenuOpened(false);
    }
  }, [isNavOpened]);

  let icon;
  if (isMobileView) {
    icon = menuDropdownIconBlack;
  } else if (isStreamPage) {
    icon = menuDropdownIcon;
  } else {
    icon = isSubMenuOpened || isScrolled ? menuDropdownIconBlack : menuDropdownIcon;
  }
  
  return (
    <nav className={navMenu}>
      {isMobileView && navStack.length > 0 ? (
        <SubMenuWithBack
          submenuData={currentLevel}
          goBack={goBack}
          goToSubmenu={goToSubmenu}
          navMenu__link={navMenu__link}
          navMenu__button={navMenu__button}
          navMenu__group={navMenu__group}
        />
      ) : (
        menu.map((item, index) => (
          <div key={`nav-item-${index}`} className={navMenu__group}>
            {item.href ? (
              <Link to={item.href} className={navMenu__link} onClick={handleLinkClick}>
                {item.label}
              </Link>
            ) : (
              <button
                className={`${navMenu__button} ${
                  index === openedSubmenuIndex ? isActive : ''
                }`}
                onClick={() =>
                  isMobileView ? goToSubmenu(item) : handleButtonClick(index)
                }
              >
                {item.label}
                {icon}
              </button>
            )}
            {!isMobileView && item.submenus && (
              <SubMenu
                submenus={item.submenus}
                className={`${index === openedSubmenuIndex ? isOpened : ''} ${
                  isNavOpened ? hasNavVisible : ''
                }`}
                ref={(el) => (submenuElements.current[index] = el)}
                {...item}
              />
            )}
          </div>
        ))
      )}
    </nav>
  );
  
});